import React, { useState, useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export const PendingVisitApprovalDialog = ({ isOpen, onClose, onApprove, pendingVisit, sites, studies }) => {
  const [approvalData, setApprovalData] = useState({
    approved_site_id: '',
    approved_study_id: '',
    visit_date: '',
    additional_notes: ''
  });

  useEffect(() => {
    if (pendingVisit) {
      // Prefer the parsed event_date from the calendar invite over "today"
      // so the QC team confirms the actual meeting date, not the time of review.
      const parsedEventDate = pendingVisit.parsed_data?.event_date;
      const defaultVisitDate = parsedEventDate
        ? new Date(parsedEventDate).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16);
      setApprovalData({
        approved_site_id: pendingVisit.matched_site?.id || '',
        approved_study_id: pendingVisit.suggested_study?.id || '',
        visit_date: defaultVisitDate,
        additional_notes: ''
      });
    }
  }, [pendingVisit]);

  const handleApprove = (e) => {
    e.preventDefault();
    const submitData = {
      approved_site_id: approvalData.approved_site_id,
      approved_study_id: approvalData.approved_study_id,
      visit_date: new Date(approvalData.visit_date).toISOString(),
      additional_notes: approvalData.additional_notes
    };
    onApprove(pendingVisit.id, submitData);
    onClose();
  };

  const handleChange = (field, value) => {
    setApprovalData(prev => ({ ...prev, [field]: value }));
  };

  if (!pendingVisit) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review and Approve Pending Visit</DialogTitle>
          <DialogDescription>Manually review and approve this visit to create tasks</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Email Information */}
          <Card className="border-blue-200">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-sm text-blue-800">Email Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 pt-4">
              <div>
                <Label className="text-sm font-medium">Subject</Label>
                <p className="text-sm text-gray-600">{pendingVisit.email_subject}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Sender</Label>
                <p className="text-sm text-gray-600">{pendingVisit.email_sender}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Date</Label>
                <p className="text-sm text-gray-600">{new Date(pendingVisit.email_date).toLocaleDateString()}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Confidence Score</Label>
                <Badge className={pendingVisit.confidence_score >= 0.8 ? 'bg-green-100 text-green-800' : 
                                 pendingVisit.confidence_score >= 0.5 ? 'bg-yellow-100 text-yellow-800' : 
                                 'bg-red-100 text-red-800'}>
                  {(pendingVisit.confidence_score * 100).toFixed(0)}%
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Extracted Information */}
          <Card className="border-green-200">
            <CardHeader className="bg-green-50">
              <CardTitle className="text-sm text-green-800">Extracted Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 pt-4">
              <div>
                <Label className="text-sm font-medium">PI Name</Label>
                <p className="text-sm text-gray-600">{pendingVisit.parsed_data?.pi_name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Study Info</Label>
                <p className="text-sm text-gray-600">{pendingVisit.parsed_data?.study_info}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Visit Type</Label>
                <p className="text-sm text-gray-600">{pendingVisit.parsed_data?.visit_type}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Patient Name</Label>
                <p className="text-sm text-gray-600">{pendingVisit.parsed_data?.patient_name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Contact</Label>
                <p className="text-sm text-gray-600">{pendingVisit.parsed_data?.contact}</p>
              </div>
            </CardContent>
          </Card>

          {/* Matching Results */}
          <Card className="border-purple-200">
            <CardHeader className="bg-purple-50">
              <CardTitle className="text-sm text-purple-800">System Matching Results</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Matched Site</Label>
                  <p className="text-sm text-gray-600">{pendingVisit.matched_site ? pendingVisit.matched_site.name : 'No automatic match found'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Suggested Study</Label>
                  <p className="text-sm text-gray-600">{pendingVisit.suggested_study ? pendingVisit.suggested_study.name : 'No suggestion available'}</p>
                </div>
              </div>
              {pendingVisit.possible_studies && pendingVisit.possible_studies.length > 0 && (
                <div className="mt-4">
                  <Label className="text-sm font-medium">Possible Studies</Label>
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    {pendingVisit.possible_studies.map((study) => (
                      <div key={study.id || study.name} className="text-sm text-gray-600 py-1 border-b border-gray-100">
                        {study.name} {study.protocol_number && `(${study.protocol_number})`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Approval Form */}
          <form onSubmit={handleApprove} className="space-y-4">
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50">
                <CardTitle className="text-sm text-orange-800">Manual Assignment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="site">Select Site *</Label>
                    <Select value={approvalData.approved_site_id} onValueChange={(value) => handleChange('approved_site_id', value)} required>
                      <SelectTrigger><SelectValue placeholder="Choose correct site" /></SelectTrigger>
                      <SelectContent>
                        {sites.map(site => (
                          <SelectItem key={site.id} value={site.id}>
                            {site.name} {site.coordinator && `(${site.coordinator})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="study">Select Study *</Label>
                    <Select value={approvalData.approved_study_id} onValueChange={(value) => handleChange('approved_study_id', value)} required>
                      <SelectTrigger><SelectValue placeholder="Choose correct study" /></SelectTrigger>
                      <SelectContent>
                        {studies
                          .filter(study => !approvalData.approved_site_id || study.sites.includes(approvalData.approved_site_id))
                          .map(study => (
                          <SelectItem key={study.id} value={study.id}>
                            {study.name} {study.protocol_number && `(${study.protocol_number})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="visit_date">Visit Date *</Label>
                    <Input id="visit_date" type="datetime-local" value={approvalData.visit_date} onChange={(e) => handleChange('visit_date', e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="additional_notes">Additional Notes</Label>
                    <Input id="additional_notes" value={approvalData.additional_notes} onChange={(e) => handleChange('additional_notes', e.target.value)} placeholder="Optional notes..." />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={!approvalData.approved_site_id || !approvalData.approved_study_id || !approvalData.visit_date}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Approve & Create Tasks
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
